import { ReportType, type SortModel } from "@/react/prop-types";
import { useGroupContext } from "@/react/providers/group-context-provider";
import { PersonStore } from "@/stores/person-store.core";
import { CustomFieldEntity } from "@laborchart-modules/common/dist/rethink/schemas/enums/custom-fields";
import type { FindPeoplePaginatedQueryParams } from "@laborchart-modules/lc-core-api";
import { Box, SplitViewCard, ToolLandingPage, useI18nContext } from "@procore/core-react";
import type { ColumnDefinition, TableApi } from "@procore/data-table";
import moment from "moment-timezone";
import * as React from "react";
import {
   useGetCustomFields,
   useGetSavedView,
   useGetGroupOptionsQuery,
} from "../common/queries/queries";
import { PersonTearsheetProvider } from "../tearsheets/people/people-tearsheet";
import { filterRendererMap } from "./people-list-filter-renderer-map";
import {
   columnHeadersMap,
   convertDataTableSort,
   convertPeopleDataTableFilters,
   defaultPeopleListTableConfig,
   filterFieldMap,
   updatePeople,
   mapApiPeopleToTablePeople,
} from "./helpers";

import { PeopleListDataTable } from "./people-list-data-table";
import type { ListServerFilter } from "../data-table/data-table-utils";
import { FileDownloadToastProvider } from "../data-table/DataExport/FileDownload";
import type { SavedView } from "@laborchart-modules/common";
import { useViewPreferenceContext } from "@/react/providers/permission-context-provider";
import { convertSavedViewToDataTableConfig, getDataForGrouping } from "@/react/shared/helper";
import { streamJobTitles } from "../data-table/data-table-utils";
import { ProjectTearsheetProvider } from "../tearsheets/project/project-tearsheet";

/* istanbul ignore next */
export function PeopleListContainer({ setTitle }: { setTitle: (title: string) => void }) {
   const { getViewPreference } = useViewPreferenceContext();
   const isLastNameFirst = getViewPreference()?.displayLastNamesFirst() ?? false;
   const { groupId } = useGroupContext();
   const I18n = useI18nContext();
   const { data: groupOptions } = useGetGroupOptionsQuery();
   const [tableApi, setTableApi] = React.useState<TableApi>();
   const { data: customFields } = useGetCustomFields(CustomFieldEntity.PERSON);
   const [convertedSavedView, setConvertedSavedView] = React.useState<any>();

   const url = new URL(window.location.href);
   const params = url.searchParams;
   const [viewId] = React.useState(params.get("viewId"));

   const handleTableReady = (api: TableApi) => setTableApi(api);

   const { data: savedViewData } = useGetSavedView(viewId);

   /* istanbul ignore next */
   React.useEffect(() => {
      if (viewId && savedViewData && customFields && !convertedSavedView) {
         const convertedSavedViewData = convertSavedViewToDataTableConfig(
            savedViewData?.data as SavedView,
            columnHeadersMap,
            defaultPeopleListTableConfig,
            filterFieldMap,
            filterRendererMap,
            customFields,
         );
         setConvertedSavedView(convertedSavedViewData);
         setTitle(savedViewData.data?.name ?? I18n.t("views.company.workforce_planning.list"));
      }
   }, [
      groupId,
      viewId,
      savedViewData,
      convertedSavedView,
      setConvertedSavedView,
      customFields,
      setTitle,
   ]);

   const fetchPeopleList = async (
      filters: ListServerFilter[],
      sortModel: SortModel[],
      startingAfter?: string,
      search?: string,
      groupKeys?: string[],
      rowGroupCols?: ColumnDefinition[],
   ) => {
      const coreApiFilters = convertPeopleDataTableFilters(filters);

      const coreApiSort = convertDataTableSort(sortModel, isLastNameFirst);
      const params: FindPeoplePaginatedQueryParams = {
         timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
         limit: 100,
         starting_after: startingAfter,
         search: search,
         sort_by: coreApiSort.sort_by,
         sort_direction: coreApiSort.direction,
         custom_field_id: coreApiSort.custom_field_id, // required when sorted by custom field
         group_id: groupId === "my-groups" ? undefined : groupId,
         filters: coreApiFilters,
      };
      const peopleData = await PersonStore.findPeopleList(params).payload;

      if (rowGroupCols && rowGroupCols?.length > 0) {
         if (peopleData.pagination?.next_starting_after) {
            let nextStartingAfter: string | null = peopleData.pagination.next_starting_after;
            do {
               try {
                  const nextData: any = await PersonStore.findPeopleList({
                     ...params,
                     starting_after: nextStartingAfter,
                     limit: 100,
                     timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                  }).payload;

                  peopleData.data.push(...nextData.data);
                  nextStartingAfter = nextData.pagination?.next_starting_after;
               } catch (error) {
                  console.error("Error fetching people list:", error);
                  break; // Exit the loop on error
               }
            } while (nextStartingAfter);
         }

         peopleData.data = await getDataForGrouping(
            peopleData.data,
            rowGroupCols,
            groupKeys,
            ReportType.PeopleList,
         );
         if (groupKeys?.length === 0) {
            return { data: peopleData.data, pagination: peopleData.data.length };
         }
      }

      const peopleListDetails = mapApiPeopleToTablePeople(
         peopleData.data,
         groupOptions ?? [],
         I18n,
      );

      return { data: peopleListDetails, pagination: peopleData.pagination };
   };

   const currentDateTime = moment().format("YYYYMMDD_HHmmss");
   return (
      <ProjectTearsheetProvider projectsTableApi={tableApi}>
         <PersonTearsheetProvider peopleTableApi={tableApi}>
            <FileDownloadToastProvider>
               <ToolLandingPage
                  className={"border-box people-list-detail-page data-table-list-detail-page"}
               >
                  <ToolLandingPage.Main className="people-list-page-main">
                     <ToolLandingPage.Body className="people-list-page-body">
                        <SplitViewCard className="people-list-card">
                           <SplitViewCard.Section className="people-list-section data-table-list-section">
                              <Box
                                 style={{
                                    marginTop: "5px",
                                    height: "100%",
                                 }}
                              >
                                 {((!viewId && groupOptions && customFields) ||
                                    (viewId &&
                                       convertedSavedView &&
                                       groupOptions &&
                                       customFields)) && (
                                    <PeopleListDataTable
                                       fetchPeopleList={fetchPeopleList}
                                       groupId={groupId}
                                       savedView={viewId ? convertedSavedView : null}
                                       currentDateTime={currentDateTime}
                                       tableApi={tableApi}
                                       handleTableReady={handleTableReady}
                                       customFields={customFields}
                                       groupOptions={groupOptions}
                                       streamJobTitles={streamJobTitles}
                                       updatePeople={(params) => updatePeople(params, customFields)}
                                       isLastNameFirst={isLastNameFirst}
                                       filterRendererMap={filterRendererMap}
                                    />
                                 )}
                              </Box>
                           </SplitViewCard.Section>
                        </SplitViewCard>
                     </ToolLandingPage.Body>
                  </ToolLandingPage.Main>
               </ToolLandingPage>
            </FileDownloadToastProvider>
         </PersonTearsheetProvider>
      </ProjectTearsheetProvider>
   );
}
